import { MdShowChart } from 'react-icons/md';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AgentApiAnalysisInsightsTrend } from '@/types/serializers';
import InsightEmptyState from './InsightEmptyState';
import { trendColorForIndex } from './insightStageDefinitions';

// Recharts は SVG の fill/stroke を受け取るためインラインで持つが、値はデザインシステムの
// セマンティック/プリミティブトークン（Semantic/Primary、Primitive/Red/600=Semantic/Accent1）と一致させる。
const BAR_COLOR = '#005FFF';
const LINE_COLOR = '#FA0000';
const SCORE_LABEL_COLOR = '#FA0000';
// X軸（月）ラベル: Semantic/Text/Sub
const AXIS_TICK_COLOR = '#6D6D6D';
// Y軸ティック番号 / (件)(日) 単位ラベル: Figma の Semantic/Text/Secondary(#88898b) 相当として
// 当プロジェクトに同等トークンがないため、最も近い Primitive/Gray/400(#888888) を採用する。
const AXIS_LABEL_COLOR = '#888888';
const GRID_COLOR = '#DFE1E1';

// Recharts の TooltipProps 型は内部実装に依存して payload プロパティが欠ける場合があるため、
// content prop に渡すコンポーネント用に最小限のローカル型を定義する。
type ChartTooltipPayloadItem = {
  dataKey?: string | number;
  value?: number | string;
  color?: string;
  name?: string | number;
  payload?: Record<string, unknown>;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: ChartTooltipPayloadItem[];
};

interface Props {
  trend: AgentApiAnalysisInsightsTrend;
  /** group_by が指定されている場合は担当者別の積み上げバーに切り替える */
  isGrouped: boolean;
}

/** バーの上に表示するスコア（件数）を整形する */
function formatScoreLabel(value: unknown): string {
  if (typeof value !== 'number' || value <= 0) return '';
  return `${value}`;
}

/**
 * バケット文字列を表示用ラベルに整形する。
 * 月: "YYYY-MM" → "M月"
 * 週: "YYYY-Wnn" → "nn週"
 * 日: "YYYY-MM-DD" → "M/D"
 */
function formatPeriodLabel(period: string): string {
  const dayMatch = period.match(/^\d{4}-(\d{1,2})-(\d{1,2})$/);
  if (dayMatch) return `${parseInt(dayMatch[1], 10)}/${parseInt(dayMatch[2], 10)}`;

  const weekMatch = period.match(/^\d{4}-W(\d{1,2})$/);
  if (weekMatch) return `${parseInt(weekMatch[1], 10)}週`;

  const monthMatch = period.match(/^\d{4}-(\d{1,2})$/);
  if (monthMatch) return `${parseInt(monthMatch[1], 10)}月`;

  return period;
}

/**
 * Figma 準拠のホバーTooltip。平均リードタイム（折線）→ 各シリーズ（バー）→ 成約合計の順で表示する。
 */
function TrendChartTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const leadTime = payload.find((p) => p.dataKey === 'averageLeadTime');
  const series = payload.filter((p) => p.dataKey !== 'averageLeadTime');
  // 非グループ時は contracts 単独、グループ時は担当者名がキーとなる
  const isGrouped = !(series.length === 1 && series[0]?.dataKey === 'contracts');
  const total = series.reduce((sum, p) => sum + (Number(p.value) || 0), 0);

  return (
    <div className="flex flex-col gap-xxs rounded-sm bg-white p-xs text-body-xs text-main shadow-layer2">
      {leadTime && (
        <div className="flex items-center gap-xxs">
          <span className="inline-block h-0.5 w-3.5 shrink-0 bg-red-600" />
          <span>平均リードタイム</span>
          <span className="font-bold">{leadTime.value}日</span>
        </div>
      )}
      {series.map((item) => (
        <div key={String(item.dataKey)} className="flex items-center gap-xxs">
          <span
            className="inline-block size-4 shrink-0 rounded-xs"
            style={{ backgroundColor: item.color }}
          />
          <span>{isGrouped ? String(item.dataKey) : '成約件数'}</span>
          <span className="font-bold">{item.value}件</span>
        </div>
      ))}
      {isGrouped && (
        <div className="flex items-center gap-md">
          <span>成約合計</span>
          <span className="font-bold">{total}件</span>
        </div>
      )}
    </div>
  );
}

/**
 * 成約件数（棒）と平均リードタイム（折線）を時系列で重ねた複合チャート。
 * グループあり時は trend.series（担当者名の配列）を凡例として使用し、
 * 各ポイントの grouped_contracts を recharts 用にスプレッドする。
 */
export default function InsightTrendChart({ trend, isGrouped }: Props) {
  // ポイントが空、もしくは全ポイントで成約件数・平均リードタイムが 0 ならデータなしとみなす
  const hasData = trend.points.some(
    (p) => (p.contracts ?? 0) > 0 || (p.average_lead_time ?? 0) > 0
  );
  if (!hasData) {
    return (
      <div className="w-full">
        <h3 className="m-0 pb-sm text-heading-xs text-main">成約件数と平均リードタイム</h3>
        <InsightEmptyState
          icon={MdShowChart}
          description="条件を変更すると、ここに推移グラフが表示されます"
        />
      </div>
    );
  }

  const groupedData = trend.points.map((p) => {
    const seriesValues = trend.series.reduce<Record<string, number>>((acc, name) => {
      acc[name] = p.grouped_contracts[name] ?? 0;
      return acc;
    }, {});
    const total = Object.values(seriesValues).reduce((sum, v) => sum + v, 0);
    return {
      period: p.period,
      averageLeadTime: p.average_lead_time,
      total,
      ...seriesValues,
    };
  });

  const flatData = trend.points.map((p) => ({
    period: p.period,
    averageLeadTime: p.average_lead_time,
    contracts: p.contracts,
  }));

  return (
    <div className="w-full [&_.recharts-surface:focus:not(:focus-visible)]:outline-hidden [&_.recharts-wrapper_*:focus:not(:focus-visible)]:outline-hidden [&_.recharts-wrapper:focus:not(:focus-visible)]:outline-hidden">
      <h3 className="m-0 pb-sm text-heading-xs text-main">成約件数と平均リードタイム</h3>

      <div className="mb-sm flex flex-wrap items-center gap-x-md gap-y-xxs text-body-sm text-main">
        {isGrouped ? (
          trend.series.map((name, idx) => (
            <div key={name} className="flex items-center gap-xs">
              <span
                className="inline-block size-[18px] shrink-0 rounded-sm"
                style={{ backgroundColor: trendColorForIndex(idx) }}
              />
              {name}
            </div>
          ))
        ) : (
          <div className="flex items-center gap-xs">
            <span className="inline-block size-[18px] shrink-0 rounded-sm bg-primary" />
            成約件数
          </div>
        )}
        <div className="flex items-center gap-xs">
          <span className="inline-block h-0.5 w-5 bg-red-600" />
          平均リードタイム
        </div>
      </div>

      <ResponsiveContainer width="100%" height={360}>
        {/* groupedData / flatData は形が異なるため Recharts の ChartData 型に合わせて単一の配列型へキャストする */}
        <ComposedChart
          data={(isGrouped ? groupedData : flatData) as Array<Record<string, unknown>>}
          margin={{ top: 24, right: 32, left: 8, bottom: 24 }}
        >
          <CartesianGrid
            yAxisId="left"
            stroke={GRID_COLOR}
            strokeDasharray="4 4"
            vertical={false}
          />
          <XAxis
            dataKey="period"
            tickFormatter={formatPeriodLabel}
            tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tickCount={6}
            allowDecimals={false}
            tick={{ fontSize: 12, fill: AXIS_LABEL_COLOR }}
            axisLine={false}
            tickLine={false}
            label={{
              value: '',
              position: 'bottom',
              offset: 12,
              dx: -8,
              fontSize: 12,
              fill: AXIS_LABEL_COLOR,
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12, fill: AXIS_LABEL_COLOR }}
            axisLine={false}
            tickLine={false}
            label={{
              value: '(日)',
              position: 'bottom',
              offset: 12,
              dx: -16,
              fontSize: 12,
              fill: AXIS_LABEL_COLOR,
            }}
          />
          <Tooltip cursor={{ fill: 'rgba(0,95,255,0.05)' }} content={<TrendChartTooltip />} />
          {isGrouped ? (
            trend.series.map((name, idx) => {
              const isTopSeries = idx === trend.series.length - 1;
              return (
                <Bar
                  key={name}
                  yAxisId="left"
                  dataKey={name}
                  stackId="contracts"
                  fill={trendColorForIndex(idx)}
                  barSize={50}
                  isAnimationActive={false}
                >
                  {isTopSeries && (
                    <LabelList
                      dataKey="total"
                      position="top"
                      formatter={formatScoreLabel}
                      fill={SCORE_LABEL_COLOR}
                      fontSize={12}
                      fontWeight={700}
                    />
                  )}
                </Bar>
              );
            })
          ) : (
            <Bar
              yAxisId="left"
              dataKey="contracts"
              fill={BAR_COLOR}
              barSize={50}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="contracts"
                position="top"
                formatter={formatScoreLabel}
                fill={SCORE_LABEL_COLOR}
                fontSize={12}
                fontWeight={700}
              />
            </Bar>
          )}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="averageLeadTime"
            stroke={LINE_COLOR}
            strokeWidth={2}
            dot={{ r: 3, fill: LINE_COLOR }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
